/**
 * TriggerMatcher — Matches EventBus events to workflow triggers
 *
 * Maintains an index of workflows by entity type for fast lookup.
 * Evaluates trigger conditions using dot-notation field paths and
 * a rich set of comparison operators.
 */

import type {
  EngineEvent,
  EngineWorkflow,
  EngineTrigger,
  TriggerCondition,
  AuditEntityType,
} from './types';
import { eventBus } from './eventBus';

export type TriggerMatchCallback = (workflow: EngineWorkflow, event: EngineEvent) => void;

class TriggerMatcher {
  /** All registered workflows */
  private workflows: Map<string, EngineWorkflow> = new Map();
  /** Index: entityType → Set of workflow IDs */
  private entityIndex: Map<string, Set<string>> = new Map();
  /** Workflows with no entityType filter (match all) */
  private globalWorkflows: Set<string> = new Set();
  /** Unsubscribe function from EventBus */
  private unsubscribe: (() => void) | null = null;

  /**
   * Register a workflow for trigger matching
   */
  register(workflow: EngineWorkflow): void {
    this.workflows.set(workflow.id, workflow);

    const entityTypes = workflow.trigger.entityTypes;
    if (entityTypes && entityTypes.length > 0) {
      for (const et of entityTypes) {
        if (!this.entityIndex.has(et)) {
          this.entityIndex.set(et, new Set());
        }
        this.entityIndex.get(et)!.add(workflow.id);
      }
    } else {
      this.globalWorkflows.add(workflow.id);
    }
  }

  /**
   * Unregister a workflow
   */
  unregister(workflowId: string): void {
    const workflow = this.workflows.get(workflowId);
    if (!workflow) return;

    this.workflows.delete(workflowId);
    this.globalWorkflows.delete(workflowId);

    for (const [, ids] of this.entityIndex) {
      ids.delete(workflowId);
    }
  }

  /**
   * Start listening to the EventBus. Calls `callback` for every matched workflow.
   */
  start(callback: TriggerMatchCallback): void {
    this.stop();
    this.unsubscribe = eventBus.on('*', (event) => {
      const matched = this.match(event);
      for (const wf of matched) {
        callback(wf, event);
      }
    });
  }

  /**
   * Stop listening to the EventBus
   */
  stop(): void {
    if (this.unsubscribe) {
      this.unsubscribe();
      this.unsubscribe = null;
    }
  }

  /**
   * Find all workflows whose trigger matches the given event
   */
  match(event: EngineEvent): EngineWorkflow[] {
    const candidateIds = new Set<string>();

    // Add entity-indexed candidates
    if (event.entityType) {
      const indexed = this.entityIndex.get(event.entityType);
      if (indexed) {
        for (const id of indexed) candidateIds.add(id);
      }
    }

    // Add global (no entity filter) candidates
    for (const id of this.globalWorkflows) {
      candidateIds.add(id);
    }

    const matched: EngineWorkflow[] = [];

    for (const id of candidateIds) {
      const wf = this.workflows.get(id);
      if (!wf || !wf.enabled) continue;

      if (this.matchesTrigger(wf.trigger, event)) {
        matched.push(wf);
      }
    }

    return matched;
  }

  /**
   * Get all registered workflow IDs
   */
  getRegisteredIds(): string[] {
    return Array.from(this.workflows.keys());
  }

  /**
   * Clear all registrations
   */
  clear(): void {
    this.workflows.clear();
    this.entityIndex.clear();
    this.globalWorkflows.clear();
  }

  // ---------------------------------------------------------------------------
  // Private
  // ---------------------------------------------------------------------------

  private matchesTrigger(trigger: EngineTrigger, event: EngineEvent): boolean {
    // Must match event type
    if (trigger.eventType !== event.type) return false;

    // Must match entity type (if specified)
    if (trigger.entityTypes && trigger.entityTypes.length > 0) {
      if (!event.entityType || !trigger.entityTypes.includes(event.entityType as AuditEntityType)) {
        return false;
      }
    }

    // Must match action (if specified)
    if (trigger.actions && trigger.actions.length > 0) {
      if (!event.action || !trigger.actions.includes(event.action)) {
        return false;
      }
    }

    // Must satisfy all conditions
    if (trigger.conditions && trigger.conditions.length > 0) {
      for (const condition of trigger.conditions) {
        if (!evaluateCondition(condition, event)) {
          return false;
        }
      }
    }

    return true;
  }
}

// =============================================================================
// CONDITION EVALUATION
// =============================================================================

/**
 * Resolve a dot-notation field path against an event object.
 * Searches event root first, then event.data.
 */
function resolveField(path: string, event: EngineEvent): unknown {
  // Try root-level first (e.g. 'entityType', 'userId')
  const rootValue = getNestedValue(event, path);
  if (rootValue !== undefined) return rootValue;

  // Try inside data
  return getNestedValue(event.data, path);
}

function getNestedValue(obj: unknown, path: string): unknown {
  if (obj == null) return undefined;
  const parts = path.split('.');
  let current: unknown = obj;
  for (const part of parts) {
    if (current == null || typeof current !== 'object') return undefined;
    current = (current as Record<string, unknown>)[part];
  }
  return current;
}

/**
 * Evaluate a single trigger condition against an event
 */
export function evaluateCondition(condition: TriggerCondition, event: EngineEvent): boolean {
  const fieldValue = resolveField(condition.field, event);

  switch (condition.operator) {
    case 'eq':
      return fieldValue === condition.value;

    case 'neq':
      return fieldValue !== condition.value;

    case 'contains':
      if (Array.isArray(fieldValue)) {
        return fieldValue.includes(condition.value);
      }
      if (typeof fieldValue === 'string' && typeof condition.value === 'string') {
        return fieldValue.includes(condition.value);
      }
      return false;

    case 'gt':
      return typeof fieldValue === 'number' && typeof condition.value === 'number'
        ? fieldValue > condition.value
        : false;

    case 'lt':
      return typeof fieldValue === 'number' && typeof condition.value === 'number'
        ? fieldValue < condition.value
        : false;

    case 'in':
      if (Array.isArray(condition.value)) {
        return condition.value.includes(fieldValue);
      }
      return false;

    case 'exists':
      return fieldValue !== undefined && fieldValue !== null;

    case 'regex':
      if (typeof fieldValue === 'string' && typeof condition.value === 'string') {
        try {
          return new RegExp(condition.value).test(fieldValue);
        } catch {
          return false;
        }
      }
      return false;

    default:
      return false;
  }
}

/**
 * Singleton TriggerMatcher instance
 */
export const triggerMatcher = new TriggerMatcher();
